import React from 'react';

import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';
import { useCurrentPlan } from '../organizations/hooks/useCurrentPlan';
import { useAPI } from './api/useAPI';
import { useAuthenticatedQuery } from './api/useAuthenticatedQuery';

export function usePlanIncludedNodes(planId?: string) {
  const { data: organization } = useCurrentOrganization();
  const { data: currentPlan, isLoading: isPlanLoading } = useCurrentPlan();
  const { client } = useAPI();

  // Use provided planId or fall back to current plan's id
  const effectivePlanId = planId || currentPlan?.id;

  const {
    data: includedNodes,
    isInitialLoading: isLoading,
    isSuccess,
    error,
  } = useAuthenticatedQuery(
    ['included-nodes', effectivePlanId],
    async () => {
      if (!organization?.id || !effectivePlanId) {
        return null;
      }

      const { data } = await client.get<string[]>(
        `/internal/orgs/${organization.id}/plans/${effectivePlanId}/included_nodes`
      );
      return data;
    },
    {
      staleTime: Infinity,
      enabled: Boolean(organization?.id && effectivePlanId),
    }
  );

  // If still loading the plan, return undefined
  const effectiveIncludedNodes = React.useMemo(() => {
    if (isPlanLoading || effectivePlanId === undefined) {
      return undefined;
    }
    return new Set(includedNodes);
  }, [isPlanLoading, effectivePlanId, includedNodes]);

  return React.useMemo(
    () =>
      [
        effectiveIncludedNodes,
        {
          isLoading: isLoading || isPlanLoading,
          isSuccess,
          error,
        },
      ] as const,
    [effectiveIncludedNodes, isLoading, isPlanLoading, isSuccess, error]
  );
}
